import { useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Card, Flex, Typography } from 'antd'
import { createStyles } from 'antd-style'
import { Copy, RefreshCw, Terminal } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from 'src/components'

import { invalidateWorkspaceApplicationDetailQueries } from '../../utils/invalidateWorkspaceApplicationQueries'

type Props = {
  apiUrl: string
  applicationId: number
  appName: string
  appToken: string
  clientId: string
}

// Single-quote shell values defensively. Inside single quotes, the shell
// interprets nothing — copy-paste is safe even if the backend ever returns
// values with spaces, `$`, `"`, `;`, etc. The only un-escapable char inside
// single quotes is `'` itself, which we encode as `'\''`.
const shellQuote = (raw: string): string => `'${raw.replace(/'/g, "'\\''")}'`

const useStyles = createStyles(({ token }) => ({
  code: {
    background: token.colorFillQuaternary,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    padding: '12px 14px',
    margin: 0,
    fontFamily: token.fontFamilyCode,
    fontSize: 12,
    lineHeight: 1.6,
    color: token.colorText,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
    overflow: 'auto',
    maxWidth: '100%'
  },
  exampleList: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  exampleItem: {
    fontFamily: token.fontFamilyCode,
    fontSize: 12,
    color: token.colorText,
    lineHeight: 1.7
  },
  framework: {
    color: token.colorTextTertiary,
    marginInlineStart: 8,
    fontFamily: token.fontFamily
  }
}))

const NPM_PROJECT_SAFE = /[^a-z0-9-]+/g
const TRIM_DASHES = /^-+|-+$/g

/**
 * npm project names must be lowercase, may contain a-z 0-9 and dashes.
 * We slugify the human app name so the scaffold command works on first try.
 */
const toCommandProjectName = (rawName: string | undefined): string => {
  const slug = (rawName ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(NPM_PROJECT_SAFE, '-')
    .replace(TRIM_DASHES, '')

  return slug || 'my-app'
}

const buildCommand = ({
  apiUrl,
  appName,
  appToken,
  clientId
}: Pick<Props, 'apiUrl' | 'appName' | 'appToken' | 'clientId'>): string => {
  const project = toCommandProjectName(appName)

  return [
    `npm create skalx-app@latest ${project} -- --yes \\`,
    `  --api-url=${shellQuote(apiUrl)} \\`,
    `  --app-token=${shellQuote(appToken)} \\`,
    `  --oidc-client-id=${shellQuote(clientId)}`
  ].join('\n')
}

const REDIRECT_URI_EXAMPLES: ReadonlyArray<{ uri: string; framework: string }> =
  [
    {
      uri: 'http://localhost:4400/oidc/callback',
      framework: 'create-skalx-app default'
    }
  ]

export const OidcQuickStartCard = (props: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const command = useMemo(
    () =>
      buildCommand({
        apiUrl: props.apiUrl,
        appName: props.appName,
        appToken: props.appToken,
        clientId: props.clientId
      }),
    [props.apiUrl, props.appName, props.appToken, props.clientId]
  )

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(command)
      message.success(t('oidc.quickstart.copied', 'Command copied'))
    } catch {
      message.error(t('oidc.quickstart.copy_failed', 'Failed to copy command'))
    }
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await invalidateWorkspaceApplicationDetailQueries(
        queryClient,
        props.applicationId
      )
    } finally {
      setIsRefreshing(false)
    }
  }

  return (
    <Card
      variant="outlined"
      size="small"
      title={
        <Flex align="center" gap={8}>
          <Terminal size={16} />
          <span>{t('oidc.quickstart.title', 'Scaffold a Skalx app')}</span>
        </Flex>
      }
      extra={
        <Flex gap={6}>
          <Button
            size="small"
            loading={isRefreshing}
            onClick={() => void handleRefresh()}
            title={t(
              'oidc.quickstart.refresh_title',
              'Refresh — picks up a freshly rotated app token'
            )}
          >
            <Flex align="center" gap={6}>
              <RefreshCw size={14} />
              <span>{t('oidc.quickstart.refresh', 'Refresh')}</span>
            </Flex>
          </Button>
          <Button size="small" onClick={() => void handleCopy()}>
            <Flex align="center" gap={6}>
              <Copy size={14} />
              <span>{t('oidc.quickstart.copy', 'Copy')}</span>
            </Flex>
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={12}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t(
            'oidc.quickstart.description',
            'Run this in your terminal to scaffold a new project wired to this app and OIDC client.'
          )}
        </Typography.Text>

        <pre className={styles.code} aria-label="quickstart command">
          {command}
        </pre>

        <Alert
          type="info"
          showIcon
          title={t(
            'oidc.quickstart.next_steps_title',
            'Next: register a redirect URI'
          )}
          description={
            <Flex vertical gap={8}>
              <Typography.Text style={{ fontSize: 12 }}>
                {t(
                  'oidc.quickstart.next_steps_body',
                  'Once your dev server is running, switch this client to Public above and add the URL where Skalx should redirect back after sign-in (Redirect URIs, one per line). Match the port your dev server actually prints in the terminal.'
                )}
              </Typography.Text>

              <Flex vertical gap={2}>
                <SectionLabel>
                  {t('oidc.quickstart.examples', 'Common dev examples')}
                </SectionLabel>
                <ul className={styles.exampleList}>
                  {REDIRECT_URI_EXAMPLES.map(({ uri, framework }) => (
                    <li key={uri} className={styles.exampleItem}>
                      {uri}
                      <span className={styles.framework}>— {framework}</span>
                    </li>
                  ))}
                </ul>
              </Flex>
            </Flex>
          }
        />
      </Flex>
    </Card>
  )
}
